/**
 * "This document type is sold with a person in the loop."
 *
 * ## The refusal this replaces
 *
 * `doctypes/doc_pricing.json` is the **self-serve allowlist** — what a stranger
 * may buy unaided (every public doctype except `CRF`; see `doctypes/doc_pricing.json`). The server's rate card
 * prices twenty-odd doctypes, and the payment gate asks that one: for `CER` in a
 * billable workspace, `_can_be_sold` resolves a real product, so `POST /runs`
 * answers **402**.
 *
 * The add-in used to walk straight into it. `docPriceOf` returns `undefined` for
 * any doctype with a null `document_price_minor`, and `paymentGate` read that as
 * *"not on sale → free"* and drafted — into a refusal it had never offered a
 * checkout for, and could not, because there is no self-serve product to sell.
 * `server/billing/gate.py` names that shape exactly: a 402 whose only remedy
 * answers 503.
 *
 * So this says so instead. It sells nothing and has no promo field — a promo
 * grants coverage for a priced product, and the point here is that there isn't
 * one. The only move is to talk to somebody, which is what "sales-assisted"
 * means. Mirrors the web wizard's `salesAssisted` branch in `NewDocumentPage`.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
} from "@fluentui/react-components";
import type { FC } from "react";

import { fonts, inkColors } from "../tokens";

const useStyles = makeStyles({
  surface: { maxWidth: "440px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "12px" },
  lede: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink, lineHeight: 1.5 },
  note: { fontFamily: fonts.body, fontSize: "11.5px", color: inkColors.ink3, lineHeight: 1.5 },
  code: { fontFamily: fonts.body, fontWeight: 600, color: inkColors.ink },
});

export const SalesAssistedDialog: FC<{
  /**
   * The doctype code that cannot be bought unaided, or `null` when closed. The
   * raw code rather than a display name: this is the string support will ask
   * for, and the catalogue's `display_name` is not in hand at the call site.
   */
  docType: string | null;
  onDismiss: () => void;
}> = ({ docType, onDismiss }) => {
  const styles = useStyles();

  return (
    <Dialog open={docType !== null} onOpenChange={(_, data) => !data.open && onDismiss()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>This submission type is sold with our team</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <span className={styles.lede}>
                <span className={styles.code}>{docType}</span> is not on the self-serve
                price list, so there is no checkout to take you through here. Drafting it
                needs a licence arranged with our team.
              </span>
              <span className={styles.note}>
                If your organisation already has one, ask us to enable this workspace and
                the paywall disappears for everyone in it.
              </span>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onDismiss}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SalesAssistedDialog;
